import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import { useSelector } from 'react-redux';
import Button from '../Button';
import Callout from 'src/components/InformationCallout/Callout';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Table from '../Table';
import { variables } from 'src/Tenants';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import ConfigureCommonAreaForm from '../../components/ConfigureCommonAreaForm';
import Search from 'src/components/Search/Search';
import useCommonAreas, {
  CommonArea,
  UseCommonAreasReturn,
} from 'src/SharedCustomHooks/UseCommonAreas/useCommonAreas';
import Skeleton from 'src/components/Skeleton/Skeleton';
import './CommonAreasTable.scss';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';

const SKELETON_ROW_COUNT: number = 13;

const skeletonColumns: any[] = [
  {
    label: 'Areas',
    accessor: 'name',
    sortable: false,
    cell: () => <Skeleton variant="text" width="60%" />,
  },
];

const skeletonData: any[] = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, index: number) => ({ id: `common-area-skeleton-${index}` })
);

interface CommonAreasTableProps {
  property: any;
}

const CommonAreasTable = ({ property }: CommonAreasTableProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const user: any = useSelector(selectAuthenticatedUser);
  const role: string = user?.roles?.[0]?.role?.name;
  const hideConfigureButton: boolean =
    role === 'Home Owner' || role === 'Occupant';

  const {
    commonAreas,
    propertyCommonAreas,
    filteredPropertyAreas,
    loading,
    isSaving,
    searchTerm,
    setSearchTerm,
    saveCommonAreas,
  }: UseCommonAreasReturn = useCommonAreas(
    property?.propertyId ?? property?.id
  );

  return (
    <>
      <div className="common-areas-retirement-callout">
        <Callout
          variant="warning"
          title="Common Areas is being retired"
          subtitle={`This feature will be removed in an upcoming release. To keep these spaces, recreate each common area as a ${variables.suite} and tag that ${variables.suite} as a common area — it will stay assigned to your ${variables.strataManagers} once this tab is gone.`}
        />
      </div>
      <div className="common-areas-table-header">
        <div className="common-areas-table-search">
          <Search
            size="small"
            placeholder="Search Common Areas..."
            onChange={(e: any) => setSearchTerm(e.target.value.trim())}
            theme={undefined}
          />
        </div>
        {hideConfigureButton ? null : (
          <Button
            color="teal"
            label="Configure Common Areas"
            onClick={() => setShowOverlay(true)}
            icon={<FontAwesomeIcon glyph="home" weight="light" size="small" />}
            size="small"
          />
        )}
      </div>
      {loading ? (
        <Table
          columns={skeletonColumns}
          data={skeletonData}
          paginate={{ itemsPerPage, position: 'both' }}
          paginationSkeleton
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : filteredPropertyAreas.length > 0 ? (
        <Table
          columns={[
            {
              label: 'Areas',
              accessor: 'name',
              cell: (data: CommonArea) => (
                <Link
                  to={{
                    pathname: PATHS.COMMON_AREA_VIEW.replace(
                      ':id',
                      data.id + '&' + property.id
                    ),
                    state: { commonArea: data, property },
                  }}
                >
                  {data && data.customLabel ? data.customLabel : data.name}
                </Link>
              ),
            },
          ]}
          data={filteredPropertyAreas}
          paginate={{ itemsPerPage, position: 'both' }}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      ) : (
        <Empty
          headline="No Common Areas yet"
          strapline="All common areas you add will be listed here so it's easier for you to manage."
          searchIsActive={searchTerm}
          cta={
            hideConfigureButton ? null : (
              <Button
                color="teal"
                onClick={() => setShowOverlay(true)}
                icon={
                  <FontAwesomeIcon glyph="home" weight="light" size="small" />
                }
                label="Configure Common Areas"
                size="small"
              />
            )
          }
          image={
            <img alt="No common areas yet" src="/assets/empty/suites.svg" />
          }
        />
      )}
      <GenericDialogue
        isOpen={showOverlay}
        onClose={() => setShowOverlay(false)}
        title="Configure Common Areas"
        description="Select the common areas available at this property and add custom labels where needed."
        maxWidth="100rem"
      >
        <ConfigureCommonAreaForm
          property={property}
          commonAreas={commonAreas}
          propertyCommonAreas={propertyCommonAreas}
          saving={isSaving}
          onSave={saveCommonAreas}
          onClose={() => setShowOverlay(false)}
        />
      </GenericDialogue>
    </>
  );
};

export default CommonAreasTable;
